export interface ParcoursFrise {
  id: string;
  slug: string;
  name: string;
  isDefault: boolean;
  offered: boolean;
  steps: string[];
}

export interface ParcoursFolder {
  id: string;
  slug: string;
  name: string;
  isDefault: boolean;
  offered: boolean;
  names: string[];
}

export interface ParcoursFamily {
  id: string;
  slug: string;
  name: string;
  frises: ParcoursFrise[];
  folders: ParcoursFolder[];
}

export function familiesOfferedForCreation(
  families: readonly ParcoursFamily[],
): ParcoursFamily[] {
  return families.filter((family) => family.frises.some((frise) => frise.offered));
}

export function creationDefaults(
  families: readonly ParcoursFamily[],
): { familySlug: string; friseSlug: string; folderSlug: string | null } | null {
  const visible = familiesOfferedForCreation(families);
  const family = visible.find((item) => item.slug === 'contentieux') ?? visible[0];
  if (!family) return null;
  const frises = family.frises.filter((frise) => frise.offered);
  const frise = frises.find((item) => item.isDefault) ?? frises[0];
  if (!frise) return null;
  const folders = family.folders.filter((folder) => folder.offered);
  const folder = folders.find((item) => item.isDefault) ?? null;
  return {
    familySlug: family.slug,
    friseSlug: frise.slug,
    folderSlug: folder?.slug ?? null,
  };
}

export function frisesOffered(family: ParcoursFamily | undefined): ParcoursFrise[] {
  return (family?.frises ?? []).filter((frise) => frise.offered);
}

export interface OfferedFrise {
  familySlug: string;
  familyName: string;
  frise: ParcoursFrise;
}

/** Toutes les frises proposées à la création, quel que soit le parcours présélectionné. */
export function offeredFrises(families: readonly ParcoursFamily[]): OfferedFrise[] {
  return families.flatMap((family) =>
    frisesOffered(family).map((frise) => ({
      familySlug: family.slug,
      familyName: family.name,
      frise,
    })),
  );
}

/** Libellé d'un bouton frise. Le parcours n'est rappelé que s'il apporte un nom différent. */
export function friseChoiceLabel(item: OfferedFrise, selectedFamilySlug: string | undefined): string {
  const sameName = item.frise.name.trim().toLocaleLowerCase('fr') === item.familyName.trim().toLocaleLowerCase('fr');
  if (item.familySlug === selectedFamilySlug || sameName) return item.frise.name;
  return `${item.frise.name} · ${item.familyName}`;
}

export function canDeleteParcoursTemplate(usedBy: number): boolean {
  return usedBy === 0;
}

export function slugifyParcoursName(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || 'modele';
}
